import React, { useState, useEffect } from "react";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Warning } from "@phosphor-icons/react";

/* ============================================================================
   CommentRequiredDialog
   ----------------------------------------------------------------------------
   Modal that forces the actor to write a comment ≥10 chars before any action
   that changes a lead's status or owner. Mirrors the strict-comment API.

   Props:
       open, onClose
       title, description, ctaLabel, ctaVariant ('default' | 'destructive')
       onConfirm(comment) → returns a Promise; we await it before closing
       extraField (optional ReactNode) — for inline picker like 'select status'
   ========================================================================== */

const MIN_LENGTH = 10;

export function CommentRequiredDialog({
    open, onClose,
    title, description,
    ctaLabel = "Confirm",
    ctaVariant = "default",
    onConfirm,
    extraField,
}) {
    const [comment, setComment] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!open) {
            setComment("");
            setBusy(false);
        }
    }, [open]);

    const valid = comment.trim().length >= MIN_LENGTH;
    const submit = async () => {
        if (!valid || busy) return;
        setBusy(true);
        try {
            await onConfirm(comment.trim());
            // parent decides when to close on success
        } catch {
            // parent surfaces toast; we just stop the spinner
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o && !busy) onClose?.(); }}>
            <DialogContent className="max-w-md" data-testid="lead-comment-required-dialog">
                <DialogHeader>
                    <DialogTitle>{title}</DialogTitle>
                    {description && <DialogDescription>{description}</DialogDescription>}
                </DialogHeader>

                {extraField}

                <div className="space-y-1.5">
                    <Label className="text-xs font-semibold">
                        Comment <span className="text-rose-600">*</span>
                    </Label>
                    <Textarea
                        rows={3}
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        placeholder="What's happening with this lead? (min 10 characters — required for accountability)"
                        className="text-sm"
                        data-testid="lead-comment-required-textarea"
                    />
                    <div className="text-[10px] text-muted-foreground flex items-center gap-1">
                        <Warning size={10} weight="fill" className="text-amber-600" />
                        {comment.trim().length < MIN_LENGTH
                            ? `${MIN_LENGTH - comment.trim().length} more character${
                                  MIN_LENGTH - comment.trim().length === 1 ? "" : "s"
                              } required`
                            : "Comment will be recorded in the audit trail."}
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={busy}
                        data-testid="lead-comment-required-cancel">
                        Cancel
                    </Button>
                    <Button
                        onClick={submit}
                        disabled={!valid || busy}
                        variant={ctaVariant === "destructive" ? "destructive" : "default"}
                        data-testid="lead-comment-required-confirm"
                    >
                        {busy ? "Saving…" : ctaLabel}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default CommentRequiredDialog;
